import ApiClient from 'api-client/ApiClient'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import ActionsSet from 'core/actions/ActionsSet'
import ListAction from 'core/actions/ListAction'
import DeleteAction from 'core/actions/DeleteAction'
import CreateAction from 'core/actions/CreateAction'
import UpdateAction from 'core/actions/UpdateAction'
import CustomAction from 'core/actions/CustomAction'
import store from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'

/**
 * Type helper for cache actions
 * Used with replaceAll/upsertAll() where items must be Record<string, unknown>[]
 */
type CacheableRecordArray = Record<string, unknown>[]
import { sessionActions } from 'core/session/sessionReducers'
import { someAsync } from 'utils/async'
import { flatten } from 'ramda'

const { dispatch } = store
const { neutron, octavia } = ApiClient.getInstance()

export const networkActions = ActionsSet.make<DataKeys.OpenstackNetworks>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackNetworks,
  cacheKey: DataKeys.OpenstackNetworks,
})

export const listNetworks = networkActions.add(
  new ListAction<DataKeys.OpenstackNetworks>(async () => {
    return neutron.getNetworks()
  })
    .addDependency(DataKeys.OpenstackAllAvailablePorts)
    .addDependency(DataKeys.ManagementTenants),
)

export const createNetwork = networkActions.add(
  new CreateAction<DataKeys.OpenstackNetworks, { body }>(async ({ body }) => {
    const created = await neutron.createNetwork(body)
    const networkId = created.id
    const newNetwork = await neutron.getNetwork(networkId)
    return newNetwork
  }),
)

export const updateNetwork = networkActions.add(
  new UpdateAction<
    DataKeys.OpenstackNetworks,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    const updatedNetwork = await neutron.updateNetwork({ id, body })
    return updatedNetwork
  }),
)

export const deleteNetwork = networkActions.add(
  new DeleteAction<DataKeys.OpenstackNetworks, { id: string }>(async ({ id }) => {
    await neutron.deleteNetwork(id)
  }),
)

export const subnetActions = ActionsSet.make<DataKeys.OpenstackSubnets>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackSubnets,
  cacheKey: DataKeys.OpenstackSubnets,
})

export const listSubnets = subnetActions.add(
  new ListAction<DataKeys.OpenstackSubnets>(async () => {
    return neutron.getSubnets()
  }),
)

export const createSubnet = subnetActions.add(
  new CreateAction<DataKeys.OpenstackSubnets, { body }>(async ({ body }) => {
    const created = await neutron.createSubnet(body)
    const subnetId = created.id
    const newSubnet = await neutron.getSubnet(subnetId)
    return newSubnet
  }),
)

export const updateSubnet = subnetActions.add(
  new UpdateAction<
    DataKeys.OpenstackSubnets,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    const updatedSubnet = await neutron.updateSubnet({ id, body })
    return updatedSubnet
  }),
)

export const deleteSubnet = subnetActions.add(
  new DeleteAction<DataKeys.OpenstackSubnets, { id: string }>(async ({ id }) => {
    await neutron.deleteSubnet(id)
  }),
)

export const networkAvailabilityActions = ActionsSet.make<DataKeys.NetworkIpAvailability>({
  uniqueIdentifier: 'network_id',
  entityName: entityNamesByKey.NetworkIpAvailability,
  cacheKey: DataKeys.NetworkIpAvailability,
})

export const listNetworkAvailability = networkAvailabilityActions.add(
  new ListAction<DataKeys.NetworkIpAvailability>(async () => {
    return neutron.getNetworkIpAvailability()
  }),
)

// Available Ports
export const availablePortsActions = ActionsSet.make<DataKeys.OpenstackAllAvailablePorts>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackAllAvailablePorts,
  cacheKey: DataKeys.OpenstackAllAvailablePorts,
})

export const listAllAvailablePorts = availablePortsActions.add(
  new ListAction<DataKeys.OpenstackAllAvailablePorts>(
    async ({ network_id }: { network_id?: string } = {}) => {
      return neutron.getAllAvailablePorts(network_id || '')
    },
  ).addDependency(DataKeys.SecurityGroups),
)

// All Ports per Network
export const portActions = ActionsSet.make<DataKeys.OpenstackPorts>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackPorts,
  cacheKey: DataKeys.OpenstackPorts,
})

export const listPorts = portActions.add(
  new ListAction<DataKeys.OpenstackPorts>(async ({ network_id }: { network_id?: string } = {}) => {
    return neutron.getPortByNetworkId(network_id || '')
  }).addDependency(DataKeys.SecurityGroups),
)

export const createPort = portActions.add(
  new CreateAction<DataKeys.OpenstackPorts, { body }>(async ({ body }) => {
    const created = await neutron.createPort(body)
    const newPort = await neutron.getPortById(created.id) // Get the updated port from CQRS endpoint
    return newPort
  }),
)

export const updatePort = portActions.add(
  new UpdateAction<
    DataKeys.OpenstackPorts,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    await neutron.updatePort({ id, body })
    const port = await neutron.getPortById(id) // Get the updated port from CQRS endpoint
    return port
  }),
)

export const deletePort = portActions.add(
  new DeleteAction<DataKeys.OpenstackPorts, { id: string }>(async ({ id }) => {
    await neutron.deletePort(id)
  }),
)

export const getPortsForVm = ({ vmId }) => {
  return neutron.getPortsForVm({ vmId })
}

export const checkNetworksApi = async () => {
  return neutron.getApis()
}

/**
 * Get total count of ports for a network (supports search/filter params for filtered counts)
 */
export interface GetPortsCountParams {
  searchField?: string
  searchValue?: string
  filters?: Record<string, string>
}

export const getPortsCount = async (
  networkId: string,
  params?: GetPortsCountParams,
): Promise<number> => {
  // Build query params for filtering
  const queryParams: string[] = []

  // Search params
  if (params?.searchField && params?.searchValue) {
    queryParams.push(`${params.searchField}=${encodeURIComponent(params.searchValue)}`)
  }

  // Filter params
  if (params?.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        queryParams.push(`${key}=${encodeURIComponent(value)}`)
      }
    })
  }

  return neutron.getPortByNetworkIdCount(networkId, queryParams.join('&'))
}

/**
 * Lightweight pagination action for ports - supports limit/marker pagination
 * with search, filter, and sort capabilities.
 * Appends to cache on subsequent pages, replaces on first page.
 */
interface ListPortsNextPageParams {
  network_id?: string
  limit?: number
  marker?: string
  // Sort params
  sort_key?: string
  sort_dir?: 'asc' | 'desc'
  // Search params
  search_field?: string
  search_value?: string
  // Filter params
  filters?: Record<string, string>
}

export const listPortsNextPage = portActions.add(
  new CustomAction<DataKeys.OpenstackPorts>(
    'listNextPage',
    async (params: ListPortsNextPageParams) => {
      const {
        network_id = '',
        limit = 10,
        marker,
        sort_key,
        sort_dir,
        search_field,
        search_value,
        filters,
      } = params || {}

      // Build query params for pagination, search, filter, and sort
      const queryParams: string[] = []
      if (limit) {
        queryParams.push(`limit=${limit}`)
      }
      if (marker) {
        queryParams.push(`marker=${marker}`)
      }
      // Sort params
      if (sort_key) {
        queryParams.push(`sort_key=${sort_key}`)
      }
      if (sort_dir) {
        queryParams.push(`sort_dir=${sort_dir}`)
      }
      // Search params - map search field to API query param
      if (search_field && search_value) {
        // Neutron API uses field name directly as query param
        queryParams.push(`${search_field}=${encodeURIComponent(search_value)}`)
      }
      // Filter params
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value !== 'all') {
            queryParams.push(`${key}=${encodeURIComponent(value)}`)
          }
        })
      }

      const ports = await neutron.getPortByNetworkId(network_id, queryParams.join('&'))

      // Note: Count is fetched separately by the hook via countAction
      // to avoid duplicate API calls. We only update pagination metadata here.

      // Calculate pagination metadata
      const nextMarker =
        ports?.length === limit && ports?.length > 0 ? ports[ports.length - 1].id : null
      const state = store.getState()
      const currentPagination = (state.session?.pagination as Record<string, any>) || {}
      const existingMeta = currentPagination?.[DataKeys.OpenstackPorts] || {}
      const totalCount = existingMeta?.totalCount // Use existing count from session
      const currentLoadedCount = state.cache?.cachedData?.[DataKeys.OpenstackPorts]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount + ports.length : ports.length
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : ports?.length === limit

      // Store pagination metadata in session
      dispatch(
        sessionActions.updateSession({
          pagination: {
            ...currentPagination,
            [DataKeys.OpenstackPorts]: {
              ...existingMeta,
              currentMarker: marker || null,
              nextMarker,
              hasMore,
              pageSize: limit,
            },
          },
        } as any),
      )

      return ports
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: any[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackPorts,
            items: (result as unknown) as CacheableRecordArray,
          }),
        )
      } else {
        // First page (no marker) - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackPorts,
            items: (result as unknown) as CacheableRecordArray,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackPorts,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const loadBalancerActions = ActionsSet.make<DataKeys.LoadBalancers>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.LoadBalancers,
  cacheKey: DataKeys.LoadBalancers,
})

export const listLoadBalancers = loadBalancerActions.add(
  new ListAction<DataKeys.LoadBalancers>(async () => {
    return octavia.getLoadBalancers()
  })
    .addDependency(DataKeys.OctaviaFlavors)
    .addDependency(DataKeys.OctaviaListeners)
    .addDependency(DataKeys.OctaviaPools)
    .addDependency(DataKeys.OctaviaHealthMonitors)
    .addDependency(DataKeys.OctaviaPoolMembers),
)

export const createLoadBalancer = loadBalancerActions.add(
  new CreateAction<DataKeys.LoadBalancers, { body }>(async ({ body }) => {
    const created = await octavia.createLoadBalancer(body)
    const loadBalancerId = created.id
    const newLoadBalancer = await octavia.getLoadBalancer(loadBalancerId)
    return newLoadBalancer
  }),
)

export const updateLoadBalancer = loadBalancerActions.add(
  new UpdateAction<
    DataKeys.LoadBalancers,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    const updatedLoadBalancer = await octavia.updateLoadBalancer({ id, body })
    return updatedLoadBalancer
  }),
)

export const deleteLoadBalancer = loadBalancerActions.add(
  new DeleteAction<DataKeys.LoadBalancers, { id: string }>(async ({ id }) => {
    await octavia.deleteLoadBalancer(id)
  }),
)

export const octaviaFlavorsActions = ActionsSet.make<DataKeys.OctaviaFlavors>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.LoadBalancers,
  cacheKey: DataKeys.OctaviaFlavors,
})

export const listOctaviaFlavors = octaviaFlavorsActions.add(
  new ListAction<DataKeys.OctaviaFlavors>(async () => {
    return octavia.getOctaviaFlavors()
  }),
)

export const listenerActions = ActionsSet.make<DataKeys.OctaviaListeners>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OctaviaListeners,
  cacheKey: DataKeys.OctaviaListeners,
})

export const listOctaviaListener = listenerActions.add(
  new ListAction<DataKeys.OctaviaListeners>(async () => {
    return octavia.getListeners()
  }),
)

export const createListener = listenerActions.add(
  new CreateAction<DataKeys.OctaviaListeners, { body }>(async ({ body }) => {
    const created = await octavia.createListener(body)
    const listenerId = created.id
    return await octavia.getListener(listenerId)
  }),
)

export const deleteListener = listenerActions.add(
  new DeleteAction<DataKeys.OctaviaListeners, { id: string }>(async ({ id }) => {
    await octavia.deleteListener(id)
  }),
)

export const poolActions = ActionsSet.make<DataKeys.OctaviaPools>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OctaviaPools,
  cacheKey: DataKeys.OctaviaPools,
})

export const listOctaviaPools = poolActions.add(
  new ListAction<DataKeys.OctaviaPools>(async () => {
    return octavia.getPools()
  }),
)

export const createPool = poolActions.add(
  new CreateAction<DataKeys.OctaviaPools, { body }>(async ({ body }) => {
    const created = await octavia.createPool(body)
    const poolId = created.id
    return await octavia.getPool(poolId)
  }),
)

export const deletePools = poolActions.add(
  new DeleteAction<DataKeys.OctaviaPools, { id: string }>(async ({ id }) => {
    await octavia.deletePools(id)
  }),
)

export const poolMembersActions = ActionsSet.make<DataKeys.OctaviaPoolMembers>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OctaviaPoolMembers,
  cacheKey: DataKeys.OctaviaPoolMembers,
})

export const listOctaviaPoolMembers = poolMembersActions.add(
  new ListAction<DataKeys.OctaviaPoolMembers, { poolIds: string[] }>(async ({ poolIds }) => {
    return someAsync(poolIds.map((poolId) => octavia.getPoolMembers(poolId))).then(flatten)
  }),
)

export const addMemberToPool = poolMembersActions.add(
  new CreateAction<DataKeys.OctaviaPoolMembers, { poolId: string; body }>(
    async ({ poolId, body }) => {
      const created = await octavia.addMemberToPool(poolId, body)
      return await octavia.getPoolMember(poolId, created.id)
    },
  ),
)

export const deleteMember = poolMembersActions.add(
  new DeleteAction<DataKeys.OctaviaPoolMembers, { poolId: string; id: string }>(
    async ({ poolId, id }) => {
      await octavia.deleteMember(poolId, id)
    },
  ),
)

export const healthMonitorsActions = ActionsSet.make<DataKeys.OctaviaHealthMonitors>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OctaviaHealthMonitors,
  cacheKey: DataKeys.OctaviaHealthMonitors,
})

export const listOctaviaHealthMonitors = healthMonitorsActions.add(
  new ListAction<DataKeys.OctaviaHealthMonitors>(async () => {
    return octavia.getHealthMonitors()
  }),
)

export const createHealthMonitor = healthMonitorsActions.add(
  new CreateAction<DataKeys.OctaviaHealthMonitors, { body }>(async ({ body }) => {
    const created = await octavia.createHealthMonitor(body)
    const healthMonitorId = created.id
    return await octavia.getHealthMonitor(healthMonitorId)
  }),
)

export const deleteHealthMonitor = healthMonitorsActions.add(
  new DeleteAction<DataKeys.OctaviaHealthMonitors, { id: string }>(async ({ id }) => {
    await octavia.deleteHealthMonitor(id)
  }),
)

// Network RBAC Policies
export const networkRBACPoliciesAction = ActionsSet.make<DataKeys.NetworkRBACPolicies>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.NetworkRBACPolicies,
  cacheKey: DataKeys.NetworkRBACPolicies,
})

export const listNetworkRBACPolicies = networkRBACPoliciesAction.add(
  new ListAction<DataKeys.NetworkRBACPolicies>(async () => {
    return neutron.getNetworkRBACPolicies()
  }).addDependency(DataKeys.ManagementTenants),
)

export const createNetworkRBACPolicy = networkRBACPoliciesAction.add(
  new CreateAction<DataKeys.NetworkRBACPolicies, { body }>(async ({ body }) => {
    return await neutron.createNetworkRBACPolicy({ body })
  }),
)

export const deleteNetworkRBACPolicy = networkRBACPoliciesAction.add(
  new DeleteAction<DataKeys.NetworkRBACPolicies, { policyId: string }>(async ({ policyId }) => {
    await neutron.deleteNetworkRBACPolicy({ policyId })
  }),
)
